/**
 * User profile hooks (task 596).
 *
 * The profile is platform data: it flows AMS → innovate-service proxy → app,
 * and is never read from the JWT. That matters beyond purity — `displayName`
 * is recomputed server-side from the profile, so the token's `given_name` can
 * be stale or absent the moment a User edits their name. Every surface that
 * addresses the User (the Dashboard greeting, the onboarding welcome, the
 * avatar initials) reads it through `useProfile`.
 *
 * One shared query key, `['me', 'profile']`, so those surfaces share a single
 * fetch and all update together when the profile is saved.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi, UserProfile, UserProfileUpdate } from '@/services/api';

/** The query key every profile-reading surface shares. */
export const PROFILE_QUERY_KEY = ['me', 'profile'] as const;

/**
 * The caller's profile. Returns the full shape even for a User who has never
 * opened their profile — `displayName` falls back to the email local part with
 * `displayNameSource: 'email'` — so consumers never branch on "no profile".
 */
export const useProfile = () =>
  useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => profileApi.get().then((res) => res.data.data),
    // Names change rarely; a fresh window of a few minutes keeps the greeting
    // from re-fetching on every navigation.
    staleTime: 5 * 60_000,
    retry: 1,
  });

/**
 * Applies a partial profile update. The server's response — not the submitted
 * values — is written into the cache, because `displayName` is derived and may
 * differ from what the User typed (an explicit display name is never
 * overwritten by a first/last name edit).
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: UserProfileUpdate) =>
      profileApi.update(update).then((res) => res.data.data),
    onSuccess: (profile: UserProfile) => {
      queryClient.setQueryData(PROFILE_QUERY_KEY, profile);
    },
  });
};

/** One per-field reason AMS rejected an update, as carried on a 422. */
export interface ProfileValidationError {
  field: string;
  message: string;
}

/**
 * Pulls the per-field errors out of a failed save so the form can render them
 * next to the inputs. A 422 from AMS (an unsupported timezone or locale) and a
 * 400 from the proxy (a wrongly-typed field) both carry
 * `details.validationErrors`; anything else is a general failure with none.
 */
export const profileValidationErrors = (error: unknown): ProfileValidationError[] => {
  const details = (error as { details?: { validationErrors?: ProfileValidationError[] } })?.details;
  return Array.isArray(details?.validationErrors) ? details.validationErrors : [];
};
